import { fetchSection, strings } from '@oo/theme';

const DESKTOP = window.matchMedia('(min-width: 860px)');
const CHANGE_DELAY = 150;
const TYPING_DELAY = 800;

function isElement(node) {
  return node?.nodeType === Node.ELEMENT_NODE;
}

// Shopify hängt section_id an Links in der Section-Antwort. Ohne Entfernen landet der Besucher auf nacktem Section-HTML.
function stripSectionId(root) {
  for (const link of root.querySelectorAll('a[href*="section_id"]')) {
    const url = new URL(link.href, window.location.origin);
    url.searchParams.delete('section_id');
    link.setAttribute('href', url.pathname + url.search + url.hash);
  }
}

// Filter, Sortierung und aktive Filter einer Kategorie- oder Suchseite ohne Neuladen.
// Ohne JavaScript bleibt das GET-Formular voll benutzbar.
class OoFacets extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('[data-oo-facets-form]');
    this.sectionId = this.dataset.sectionId;
    if (!this.form || !this.sectionId) return;

    this.dialog = this.querySelector('[data-oo-facets-dialog]');
    this.drawerBody = this.querySelector('[data-oo-facets-slot]');
    this.home = this.querySelector('[data-oo-facets-home]');
    this.live = this.querySelector('[data-oo-facets-live]');
    this.dialogLive = this.querySelector('[data-oo-facets-live-dialog]');
    this.loading = this.querySelector('[data-oo-facets-loading]');
    this.search = window.location.search;
    // Aufgeklappte Listen und Suchtexte je Filtergruppe, damit sie das Neuzeichnen nach jedem Haken überstehen.
    this.expanded = new Set();
    this.finds = new Map();

    this.onChange = (event) => {
      if (this.owns(event.target)) this.schedule(event.target.type === 'number' ? 0 : CHANGE_DELAY);
    };
    this.onInput = (event) => {
      if (event.target.matches?.('[data-oo-facets-find]')) {
        this.find(event.target);
        return;
      }
      if (this.owns(event.target) && event.target.type === 'number') this.schedule(TYPING_DELAY);
    };
    this.onSubmit = (event) => {
      if (event.target !== this.form) return;
      event.preventDefault();
      this.update(this.buildUrl());
    };
    this.onClick = (event) => this.handleClick(event);
    this.onPopState = () => this.handlePopState();
    this.onDialogOpen = (event) => {
      if (event.target === this.dialog) this.drawerBody?.append(...this.panels());
    };
    this.onDialogClose = () => this.home?.append(...this.panels());
    this.onViewport = () => {
      if (DESKTOP.matches && this.dialog?.open) this.dialog.close();
    };

    this.addEventListener('change', this.onChange);
    this.addEventListener('input', this.onInput);
    this.addEventListener('submit', this.onSubmit);
    this.addEventListener('click', this.onClick);
    this.addEventListener('oo:dialog:open', this.onDialogOpen);
    this.dialog?.addEventListener('close', this.onDialogClose);
    DESKTOP.addEventListener('change', this.onViewport);
    window.addEventListener('popstate', this.onPopState);
  }

  disconnectedCallback() {
    if (!this.onChange) return;
    this.removeEventListener('change', this.onChange);
    this.removeEventListener('input', this.onInput);
    this.removeEventListener('submit', this.onSubmit);
    this.removeEventListener('click', this.onClick);
    this.removeEventListener('oo:dialog:open', this.onDialogOpen);
    this.dialog?.removeEventListener('close', this.onDialogClose);
    DESKTOP.removeEventListener('change', this.onViewport);
    window.removeEventListener('popstate', this.onPopState);
    clearTimeout(this.timer);
    this.controller?.abort();
  }

  owns(element) {
    return element instanceof HTMLElement && 'form' in element && element.form === this.form;
  }

  panels() {
    return this.querySelectorAll('[data-oo-part="panel"]');
  }

  schedule(delay) {
    clearTimeout(this.timer);
    this.timer = setTimeout(() => this.update(this.buildUrl()), delay);
  }

  buildUrl() {
    const params = new URLSearchParams();
    for (const [key, value] of new FormData(this.form)) {
      if (typeof value === 'string' && value.trim() !== '') params.append(key, value.trim());
    }
    const url = new URL(this.form.getAttribute('action'), window.location.origin);
    url.search = params.toString();
    return url.toString();
  }

  handleClick(event) {
    const more = event.target.closest('[data-oo-facets-more]');
    if (more && this.contains(more)) {
      const group = more.closest('[data-oo-facets-group]');
      const expand = group.hasAttribute('data-oo-facets-collapsed');
      this.setExpanded(group, expand);
      if (expand) this.expanded.add(group.dataset.ooFacetsGroup);
      else this.expanded.delete(group.dataset.ooFacetsGroup);
      return;
    }
    const link = event.target.closest('a[data-oo-facets-link]');
    if (!link || !this.contains(link)) return;
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
      return;
    }
    event.preventDefault();
    this.update(link.href);
  }

  handlePopState() {
    if (window.location.search === this.search) return;
    const action = new URL(this.form.getAttribute('action'), window.location.origin);
    if (action.pathname !== window.location.pathname) return;
    this.search = window.location.search;
    this.update(window.location.href, { push: false });
  }

  async update(url, { push = true } = {}) {
    clearTimeout(this.timer);
    this.controller?.abort();
    const controller = new AbortController();
    this.controller = controller;
    this.setBusy(true);
    try {
      const doc = await fetchSection(url, this.sectionId, { signal: controller.signal });
      // Jede echte Antwort enthält die Trefferliste (oo-facets.liquid). Fehlt sie (z. B. Kategorie inzwischen
      // gelöscht: Shopify antwortet trotzdem mit 200), vor jeder Änderung abbrechen und die ganze Seite laden.
      if (!doc.querySelector('[data-oo-part="results"]')) throw new Error('unexpected section');
      this.render(doc);
      if (push) {
        const target = new URL(url, window.location.origin);
        target.searchParams.delete('section_id');
        window.history.pushState({ ooFacets: true }, '', target);
      }
      this.search = window.location.search;
      this.announce();
      // Für Code und App-Embeds, die neu eingesetzte Produktkarten nachbearbeiten müssen.
      this.dispatchEvent(new CustomEvent('oo:facets:updated', { bubbles: true }));
    } catch (error) {
      if (error.name === 'AbortError') return;
      window.location.assign(url);
    } finally {
      if (this.controller === controller) {
        this.controller = null;
        this.setBusy(false);
      }
    }
  }

  render(doc) {
    const active = document.activeElement;
    const hadFocus = this.contains(active);
    const focusId = hadFocus ? active.id : '';
    const openGroups = new Set(
      Array.from(this.querySelectorAll('details[data-oo-facets-group][open]'), (group) => group.dataset.ooFacetsGroup),
    );

    for (const target of this.querySelectorAll('[data-oo-part]')) {
      const name = target.dataset.ooPart;
      const source = doc.querySelector(`[data-oo-part="${CSS.escape(name)}"]`);
      target.hidden = source ? source.hidden : true;
      if (name === 'panel') {
        this.renderPanel(target, source);
      } else {
        target.replaceChildren(...(source ? source.childNodes : []));
      }
    }

    for (const group of this.querySelectorAll('details[data-oo-facets-group]')) {
      const key = group.dataset.ooFacetsGroup;
      if (openGroups.has(key)) group.open = true;
      if (this.expanded.has(key)) this.setExpanded(group, true);
      const input = group.querySelector('[data-oo-facets-find]');
      if (input && this.finds.has(key)) {
        input.value = this.finds.get(key);
        this.find(input);
      }
    }
    stripSectionId(this);

    if (hadFocus && !this.contains(document.activeElement)) {
      const same = focusId ? document.getElementById(focusId) : null;
      same?.focus({ preventScroll: true });
      if (!this.contains(document.activeElement)) {
        // Bei offenem Modal ist die Ergebnis-Überschrift inert; der Fokus bleibt dann im Dialog.
        const fallback = this.dialog?.open
          ? this.dialog.querySelector('[data-oo-part="apply"]')
          : this.querySelector('[data-oo-facets-focus]');
        fallback?.focus({ preventScroll: true });
      }
    }
  }

  // Die Preisgruppe, in die gerade getippt wird, bleibt stehen, sonst gingen Eingabe und Cursor verloren.
  renderPanel(target, source) {
    const nodes = source ? Array.from(source.childNodes) : [];
    const active = document.activeElement;
    const keep =
      active instanceof HTMLInputElement && active.type === 'number' && target.contains(active)
        ? active.closest('[data-oo-facets-group]')
        : null;
    const index = keep
      ? nodes.findIndex((node) => isElement(node) && node.dataset.ooFacetsGroup === keep.dataset.ooFacetsGroup)
      : -1;
    if (index === -1) {
      target.replaceChildren(...nodes);
      return;
    }
    for (const child of Array.from(target.childNodes)) {
      if (child !== keep) child.remove();
    }
    keep.before(...nodes.slice(0, index));
    keep.after(...nodes.slice(index + 1));
  }

  // Lange Filterlisten zeigen erst wenige Werte (oo-facets.liquid kürzt nur mit JavaScript, ohne bleibt alles sichtbar).
  setExpanded(group, expand) {
    group.toggleAttribute('data-oo-facets-collapsed', !expand);
    const button = group.querySelector('[data-oo-facets-more]');
    if (!button) return;
    button.setAttribute('aria-expanded', String(expand));
    button.textContent = expand ? button.dataset.labelLess : button.dataset.labelMore;
  }

  // Sucht in einer langen Filterliste. Während der Suche zählen alle Werte, nicht nur die ersten.
  find(input) {
    const group = input.closest('[data-oo-facets-group]');
    if (!group) return;
    const query = input.value.trim().toLocaleLowerCase();
    let hits = 0;
    for (const item of group.querySelectorAll('[data-oo-facets-value]')) {
      const match = query === '' || item.dataset.ooFacetsValue.includes(query);
      item.hidden = !match;
      if (match) hits += 1;
    }
    group.classList.toggle('is-finding', query !== '');
    const empty = group.querySelector('[data-oo-facets-find-empty]');
    if (empty) empty.hidden = hits > 0;
    const more = group.querySelector('[data-oo-facets-more]');
    if (more) more.hidden = query !== '';
    if (query === '') this.finds.delete(group.dataset.ooFacetsGroup);
    else this.finds.set(group.dataset.ooFacetsGroup, input.value);
  }

  setBusy(busy) {
    this.classList.toggle('is-loading', busy);
    this.querySelector('[data-oo-part="results"]')?.setAttribute('aria-busy', String(busy));
    if (this.loading) this.loading.hidden = !busy;
    const live = this.liveRegion();
    if (busy && live) live.textContent = strings.loading || '';
  }

  announce() {
    const live = this.liveRegion();
    if (!live) return;
    const count = this.querySelector('[data-oo-part="count"]');
    live.textContent = count ? count.textContent.trim() : '';
  }

  // Ein offenes Modal macht die Region außerhalb inert, Screenreader sagen sie dann nicht an.
  liveRegion() {
    return this.dialog?.open && this.dialogLive ? this.dialogLive : this.live;
  }
}

// Schiebt die aktive Kategorie in der mobilen Schnellwahl ins Bild, ohne die Seite zu scrollen.
class OoQuickpick extends HTMLElement {
  connectedCallback() {
    const list = this.querySelector('[data-oo-quickpick-list]');
    const current = list?.querySelector('[aria-current="page"]');
    if (!current || list.scrollWidth <= list.clientWidth) return;
    list.scrollLeft = Math.max(0, current.offsetLeft - 12);
  }
}

if (!customElements.get('oo-facets')) customElements.define('oo-facets', OoFacets);
if (!customElements.get('oo-quickpick')) customElements.define('oo-quickpick', OoQuickpick);
